import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import routes from 'constants/routes';

interface Props {
	children: ReactNode;
}

interface State {
	hasError: boolean;
	errorMessage: string | null;
}

export class DashboardErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, errorMessage: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, errorMessage: error?.message ?? 'Error desconocido' };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		console.error('[DashboardErrorBoundary]', error, info);
	}

	handleRetry = (): void => {
		this.setState({ hasError: false, errorMessage: null });
	};

	render(): ReactNode {
		if (this.state.hasError) {
			return (
				<Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
					<Typography variant="h5" fontWeight={700} color="error" mb={2}>
						Algo salió mal en esta pantalla
					</Typography>
					{this.state.errorMessage && (
						<Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
							{this.state.errorMessage}
						</Alert>
					)}
					<Typography variant="body2" color="text.secondary" mb={4}>
						Podés reintentar cargar la pantalla o volver al inicio. Si el problema persiste, probablemente sea un error
						temporal del servidor.
					</Typography>
					<Box display="flex" gap={2} justifyContent="center">
						<Button variant="outlined" startIcon={<RefreshIcon />} onClick={this.handleRetry}>
							Reintentar
						</Button>
						<Button variant="contained" startIcon={<HomeIcon />} href={routes.home}>
							Volver al inicio
						</Button>
					</Box>
				</Container>
			);
		}

		return this.props.children;
	}
}
